/**
 * Money the platform is sitting on.
 *
 * WHY expected_release_at gets its own emphasis: a reserve past its release date is R9
 * (RESERVE_OVERDUE) — the seller is owed money that nobody has flagged. This page is where that
 * becomes visible before the engine's nightly run says so.
 */
import { useState, type ReactNode } from "react";
import { api } from "../api";
import { Amount } from "../components/Amount";
import { Badge, StatusBadge } from "../components/Badges";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { deadlineLabel, deadlineTone, formatDateTime } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

export function HeldFunds(): ReactNode {
  const store = useStore();
  const [heldOnly, setHeldOnly] = useState(true);
  const state = useAsync(() => api.heldFunds(store.id, { held: heldOnly }), [store.id, heldOnly]);

  return (
    <>
      <header className="page-head">
        <h1 className="page-title">Held funds</h1>
        <div className="filters" role="group" aria-label="Held funds filter">
          <button type="button" className={`filter${heldOnly ? " filter-active" : ""}`} onClick={() => setHeldOnly(true)}>HELD</button>
          <button type="button" className={`filter${heldOnly ? "" : " filter-active"}`} onClick={() => setHeldOnly(false)}>ALL</button>
        </div>
      </header>

      {state.status === "loading" ? <Loading label="Loading held funds" /> : null}
      {state.status === "error" ? <ErrorState error={state.error} onRetry={state.reload} /> : null}
      {state.status === "ready" ? (
        state.data.held_funds.length === 0 ? (
          <EmptyState
            title={heldOnly ? "Nothing is being held" : "No holds recorded"}
            hint="Reserves, dispute holds and risk holds appear here with the date they are expected to be released."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Kind</th><th>Status</th><th className="num">Amount</th><th>Held at</th><th>Expected release</th><th>Released</th><th>Reference</th><th>Order</th></tr>
              </thead>
              <tbody>
                {state.data.held_funds.map((h) => {
                  const tone = h.status === "HELD" ? deadlineTone(h.expected_release_at) : "NONE";
                  return (
                    <tr key={h.id} className={tone === "PAST" ? "row-warn" : ""}>
                      <td>{h.kind}</td>
                      <td><StatusBadge status={h.status} /></td>
                      <td className="num"><Amount minor={h.amount_minor} currency={h.currency} strong /></td>
                      <td>{formatDateTime(h.held_at)}</td>
                      <td>
                        {h.expected_release_at ? (
                          <>
                            <div>{formatDateTime(h.expected_release_at)}</div>
                            {tone === "PAST" ? <Badge tone="warn">{deadlineLabel(h.expected_release_at)}</Badge> : null}
                          </>
                        ) : <span className="muted">unknown</span>}
                      </td>
                      <td>{h.released_at ? formatDateTime(h.released_at) : <span className="muted">—</span>}</td>
                      <td className="mono" title={h.external_ref}>{h.dispute_external_id ?? h.external_ref.slice(-16)}</td>
                      <td>{h.order_number ?? <span className="muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </>
  );
}
